<template>
  <main class="px-3">
    <Header />
    <CreditPullDisplay
      @accept="acceptBtn"
      :docHtml="docHtml"
      :acceptDisabled="acceptDisabled"
      :showAlert="showAlert"
      @signatureDrawn="signatureDrawn"
      @signatureCleared="signatureCleared"
      @alertClosed="alertClosed"
    />
  </main>

  <LoadingOverlay :loading="isLoading" />
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { useRouter } from "vue-router";
import { setLanguage } from "@/apps/credit-line/helpers/translations.helper";
import Header from "@/shared-components/consumer/Header.vue";
import CreditPullDisplay from "@/shared-components/consumer/CreditPullDisplay.vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import LoadingOverlay from "@/shared-components/ui/core-app/LoadingOverlay.vue";
import {
  ERROR_CODES,
  ErrorHandler,
} from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import {
  FeatureControlService,
  AvailableFeatures,
  Country,
} from "@/apps/credit-line/services/feature-control.service";
import { Service as CreditLineApiService } from "@/apps/credit-line/services/network.service";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import logAnalyticsEvent, {
  coreAppOnboardingCategoryName,
} from "@/apps/credit-line/services/google-analytics-events.service";

declare const Android: AndroidInterface;

export default defineComponent({
  name: "CreditLineCheckCreditPullView",
  components: {
    Header,
    LoadingOverlay,
    CreditPullDisplay,
  },
  beforeMount() {
    setLanguage(this);
  },
  async mounted() {
    const creditPullEnabled = await this.isCreditPullEnabled();
    if (!creditPullEnabled) {
      this.router.push({
        name: "CreditLineRegistrationView",
      });
      return;
    }
    await this.fetchCreditPullDocument();
    this.isLoading = false;

    if (isAndroid() && typeof Android?.finishedLoading === "function") {
      Android.finishedLoading();
    }
  },
  setup() {
    const { t } = useI18n();
    const router = useRouter();
    const featureControlService = new FeatureControlService();
    const creditLineApiService = new CreditLineApiService();
    const isLoading = ref<boolean>(true);
    const deviceTag = AuthenticationHelper.getDeviceTag();
    const docHtml = ref<string>("");
    const acceptDisabled = ref<boolean>(true);
    const showAlert = ref<boolean>(false);

    const acceptBtn = async (base64Signature) => {
      isLoading.value = true;
      showAlert.value = false;
      acceptDisabled.value = true;
      const signature = base64Signature as string;
      const signType = "Drawing";
      try {
        const res = await creditLineApiService.acceptSignature({
          signature,
          deviceTag: deviceTag,
          source: "prior_borrower",
        });
        if (res.data.message === "Success" && !res.data.errorCode) {
          // Track when signed and type of signature
          logAnalyticsEvent(
            AnalyticEvents.CoreAppOnboarding.OnboardingCreditPullSigned,
            coreAppOnboardingCategoryName,
            "",
            { signType: signType }
          );
          navigateToPriorBorrowerOffers();
        }
      } catch (error) {
        console.error(error);
        ErrorHandler.logAPIError(error, ERROR_CODES.CREDIT_PULL_NETWORK_ERROR);
      }
      isLoading.value = false;
      acceptDisabled.value = false;
      showAlert.value = true;
    };

    const signatureDrawn = () => {
      acceptDisabled.value = false;
    };

    const signatureCleared = () => {
      acceptDisabled.value = true;
    };

    const fetchCreditPullDocument = async () => {
      isLoading.value = true;
      try {
        const res = await creditLineApiService.creditCheckPermission({
          deviceTag: deviceTag,
          source: "prior_borrower",
        });
        if (res.data.isSigned || res.data.canSkipSignature) {
          navigateToPriorBorrowerOffers();
          return;
        }
        docHtml.value = res.data.document ?? "";
        // Log Screen View into GA
        logAnalyticsEvent(
          AnalyticEvents.CoreAppOnboarding.OnboardingCreditPullScreenView,
          coreAppOnboardingCategoryName
        );
      } catch (error) {
        console.error(error);
        ErrorHandler.logAPIError(error, ERROR_CODES.PERMISSIONS_NETWORK_ERROR);
      }
      isLoading.value = false;
    };

    const navigateToPriorBorrowerOffers = () => {
      router.push({
        name: "CreditLineRegistrationView",
        query: {
          source: "prior_borrower",
        },
      });
    };

    const isCreditPullEnabled = async () => {
      try {
        const countryRes = await creditLineApiService.getCountry({
          deviceTag: deviceTag,
        });
        const country = countryRes.data.country.toUpperCase() as Country;
        return featureControlService.isFeatureEnabled(
          AvailableFeatures.PB_CREDIT_PULL,
          country
        );
      } catch (error) {
        console.error(error);
        return false;
      }
    };

    const alertClosed = () => {
      showAlert.value = false;
    };

    return {
      t,
      isLoading,
      docHtml,
      featureControlService,
      router,
      acceptBtn,
      fetchCreditPullDocument,
      isCreditPullEnabled,
      acceptDisabled,
      signatureDrawn,
      signatureCleared,
      showAlert,
      alertClosed,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
</style>
